import { useMemo } from "react";
import { JSX } from "react";

import {
  MedicalPlanServices,
  MedicalPlanDeductibleAndOutOfPocketDetails,
  MedicalPlan,
  PlanRates,
  MedicalCoverage,
} from "../../constants/aquafina";
import CollapsableSection from "../sections/collapsable-section";
import {
  RangeIterator,
  FormatDate,
  OutOfNetworkDefaultValue,
} from "../../utils";
import { CoverageHeaderRow, CoverageRow, NotProvidedRow } from "../table/rows";
import {
  CoverageCell,
  CoverageVectorCell,
  CoverageVectorHeaderCell,
} from "../table/cells";
import Rates from "../rates";

type Props = {
  coverage: MedicalCoverage;
};

function Medical({ coverage }: Props): JSX.Element {
  const state = useMemo(() => {
    const plans: MedicalPlan[] = [];
    const medicalDeductibleData: MedicalPlanDeductibleAndOutOfPocketDetails[] =
      [];
    const services: MedicalPlanServices[] = [];
    const rates: PlanRates[] = [];
    let inconsistentRateData = false;
    coverage.plans.forEach((plan) => {
      plans.push(plan);
      medicalDeductibleData.push(plan.deductibleAndOutOfPocketDetails!);
      services.push(plan.services!);
      rates.push(plan.planRates);
      if (plan.inconsistentRateData === true) {
        inconsistentRateData = true;
      }
    });

    return {
      plans,
      medicalDeductibleData,
      services,
      rates,
      inconsistentRateData,
    };
  }, [coverage]);

  return (
    <>
      <MedicalPlanBasics
        plans={state.plans}
        originalEffectiveDate={coverage.originalEffectiveDate}
      />
      <MedicalDeductibleAndOutOfPocket
        fundingType={coverage.fundingType}
        data={state.medicalDeductibleData}
      />
      <MedicalServices
        fundingType={coverage.fundingType}
        services={state.services}
      />
      <MedicalHospitalFacilityServices
        fundingType={coverage.fundingType}
        services={state.services}
      />
      <Rates
        rates={state.rates}
        inconsistentRateData={state.inconsistentRateData}
        totalPlans={
          state.rates.length === 0 ? state.plans.length : state.rates.length
        }
      />
    </>
  );
}

const MedicalPlanBasics = ({
  plans,
  originalEffectiveDate,
}: {
  plans: MedicalPlan[];
  originalEffectiveDate?: string | null;
}): JSX.Element => (
  <CollapsableSection numberOfColumns={plans.length} title="Plan Basics">
    <MedicalPlanBasicsData
      plans={plans}
      originalEffectiveDate={originalEffectiveDate}
    />
  </CollapsableSection>
);

const MedicalPlanBasicsData = ({
  plans,
  originalEffectiveDate,
}: {
  plans: MedicalPlan[];
  originalEffectiveDate?: string | null;
}): JSX.Element => (
  <>
    <CoverageRow label="Plan Name" isWhite>
      {RangeIterator(plans.length, (index) => (
        <CoverageCell value={plans[index].basics?.name} key={index} />
      ))}
    </CoverageRow>
    <CoverageRow label="Plan Identifier">
      {RangeIterator(plans.length, (index) => (
        <CoverageCell value={plans[index].basics?.planIdentifier} key={index} />
      ))}
    </CoverageRow>
    <CoverageRow label="Original Effective Date" isWhite>
      {RangeIterator(plans.length, (index) => (
        <CoverageCell
          key={index}
          value={FormatDate(originalEffectiveDate ?? "")}
        />
      ))}
    </CoverageRow>
    <CoverageRow label="Most Recent Effective Date">
      {RangeIterator(plans.length, (index) => (
        <CoverageCell
          value={FormatDate(plans[index].basics?.effectiveDate)}
          key={index}
        />
      ))}
    </CoverageRow>
    <CoverageRow label="Termination Rule" isWhite>
      {RangeIterator(plans.length, (index) => (
        <CoverageCell
          value={plans[index].basics?.terminationRule}
          key={index}
        />
      ))}
    </CoverageRow>
    <CoverageRow label="Funding Type">
      {RangeIterator(plans.length, (index) => (
        <CoverageCell value={plans[index].basics?.fundingType} key={index} />
      ))}
    </CoverageRow>
    <CoverageRow label="Metallic Level" isWhite>
      {RangeIterator(plans.length, (index) => (
        <CoverageCell value={plans[index].basics?.metallicLevel} key={index} />
      ))}
    </CoverageRow>
  </>
);

const IN_OUT_NETWORK_LABELS: [string, string] = [
  "In Network",
  "Out of Network",
];

type MedicalDeductibleAndOutOfPocketProps = {
  data: MedicalPlanDeductibleAndOutOfPocketDetails[];
  fundingType: string | null;
};

const MedicalDeductibleAndOutOfPocket = ({
  data,
  fundingType,
}: MedicalDeductibleAndOutOfPocketProps): JSX.Element => (
  <CollapsableSection
    numberOfColumns={data.length}
    title="Deductible & Out of Pocket Details"
  >
    <MedicalDeductibleAndOutOfPocketData
      fundingType={fundingType}
      data={data}
    />
  </CollapsableSection>
);

const MedicalDeductibleAndOutOfPocketData = ({
  data,
  fundingType,
}: MedicalDeductibleAndOutOfPocketProps): JSX.Element => {
  if (data.every((deductible) => deductible === null)) {
    return <NotProvidedRow colSpan={data.length} />;
  }
  return (
    <>
      <CoverageHeaderRow isWhite>
        {RangeIterator(data.length, (index) => (
          <CoverageVectorHeaderCell label={IN_OUT_NETWORK_LABELS} key={index} />
        ))}
      </CoverageHeaderRow>
      <CoverageRow label="Individual Deductible">
        {RangeIterator(data.length, (index) => (
          <CoverageVectorCell
            value={[
              data[index]?.inNetwork?.individualDeductible,
              data[index]?.outOfNetwork?.individualDeductible ||
                OutOfNetworkDefaultValue(
                  data[index]?.outOfNetwork,
                  fundingType,
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Family Deductible" isWhite>
        {RangeIterator(data.length, (index) => (
          <CoverageVectorCell
            value={[
              data[index]?.inNetwork?.familyDeductible,
              data[index]?.outOfNetwork?.familyDeductible ||
                OutOfNetworkDefaultValue(
                  data[index]?.outOfNetwork,
                  fundingType,
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Is Deductible Embedded in Out-of-Pocket Max?">
        {RangeIterator(data.length, (index) => (
          <CoverageCell
            value={data[index]?.isDeductibleCalculatedIntoOutOfPocketMax}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Individual Out-of-Pocket Max" isWhite>
        {RangeIterator(data.length, (index) => (
          <CoverageVectorCell
            value={[
              data[index]?.inNetwork?.individualOutOfPocketMax,
              data[index]?.outOfNetwork?.individualOutOfPocketMax ||
                OutOfNetworkDefaultValue(
                  data[index]?.outOfNetwork,
                  fundingType,
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Family Out-of-Pocket Max">
        {RangeIterator(data.length, (index) => (
          <CoverageVectorCell
            value={[
              data[index]?.inNetwork?.familyOutOfPocketMax,
              data[index]?.outOfNetwork?.familyOutOfPocketMax ||
                OutOfNetworkDefaultValue(
                  data[index]?.outOfNetwork,
                  fundingType,
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Deductible Duration" isWhite>
        {RangeIterator(data.length, (index) => (
          <CoverageCell value={data[index]?.deductibleTimePeriod} key={index} />
        ))}
      </CoverageRow>
    </>
  );
};

type MedicalServicesProps = {
  services: MedicalPlanServices[];
  fundingType: string | null;
};

const MedicalServices = ({
  services,
  fundingType,
}: MedicalServicesProps): JSX.Element => (
  <CollapsableSection numberOfColumns={services.length} title="Services">
    <MedicalServicesData services={services} fundingType={fundingType} />
  </CollapsableSection>
);

const MedicalServicesData = ({
  services,
  fundingType,
}: MedicalServicesProps): JSX.Element => {
  if (services.every((service) => service === null)) {
    return <NotProvidedRow colSpan={services.length} />;
  }
  return (
    <>
      <CoverageHeaderRow isWhite>
        {RangeIterator(services.length, (index) => (
          <CoverageVectorHeaderCell label={IN_OUT_NETWORK_LABELS} key={index} />
        ))}
      </CoverageHeaderRow>
      <CoverageRow label="Office Visit (Primary Care)">
        {RangeIterator(services.length, (index) => (
          <CoverageVectorCell
            value={[
              services[index]?.inNetwork?.officeVisitPrimaryCare,
              services[index]?.outOfNetwork?.officeVisitPrimaryCare ||
                OutOfNetworkDefaultValue(
                  services[index]?.outOfNetwork,
                  fundingType,
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Office Visit (Specialist)" isWhite>
        {RangeIterator(services.length, (index) => (
          <CoverageVectorCell
            value={[
              services[index]?.inNetwork?.officeVisitSpecialist,
              services[index]?.outOfNetwork?.officeVisitSpecialist ||
                OutOfNetworkDefaultValue(
                  services[index]?.outOfNetwork,
                  fundingType,
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Urgent Care Visit">
        {RangeIterator(services.length, (index) => (
          <CoverageVectorCell
            value={[
              services[index]?.inNetwork?.urgentCareVisit,
              services[index]?.outOfNetwork?.urgentCareVisit ||
                OutOfNetworkDefaultValue(
                  services[index]?.outOfNetwork,
                  fundingType,
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Diagnostic Laboratory and X-Ray" isWhite>
        {RangeIterator(services.length, (index) => (
          <CoverageVectorCell
            value={[
              services[index]?.inNetwork.diagnosticLaboratory,
              services[index]?.outOfNetwork?.diagnosticLaboratory ||
                OutOfNetworkDefaultValue(
                  services[index]?.outOfNetwork,
                  fundingType,
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
    </>
  );
};

const MedicalHospitalFacilityServices = ({
  services,
  fundingType,
}: MedicalServicesProps): JSX.Element => (
  <CollapsableSection
    numberOfColumns={services.length}
    title="Hospital/Facility Services"
  >
    <MedicalHospitalFacilityServicesData
      fundingType={fundingType}
      services={services}
    />
  </CollapsableSection>
);

const MedicalHospitalFacilityServicesData = ({
  services,
  fundingType,
}: MedicalServicesProps): JSX.Element => {
  if (services.every((service) => service === null)) {
    return <NotProvidedRow colSpan={services.length} />;
  }
  return (
    <>
      <CoverageHeaderRow isWhite>
        {RangeIterator(services.length, (index) => (
          <CoverageVectorHeaderCell label={IN_OUT_NETWORK_LABELS} key={index} />
        ))}
      </CoverageHeaderRow>
      <CoverageRow label="Inpatient Care">
        {RangeIterator(services.length, (index) => (
          <CoverageVectorCell
            value={[
              services[index]?.inNetwork?.inpatientCare,
              services[index]?.outOfNetwork?.inpatientCare ||
                OutOfNetworkDefaultValue(
                  services[index]?.outOfNetwork,
                  fundingType,
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Outpatient Care" isWhite>
        {RangeIterator(services.length, (index) => (
          <CoverageVectorCell
            value={[
              services[index]?.inNetwork?.outpatientCare,
              services[index]?.outOfNetwork?.outpatientCare ||
                OutOfNetworkDefaultValue(
                  services[index]?.outOfNetwork,
                  fundingType,
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Emergency Room (Waived if Admitted)">
        {RangeIterator(services.length, (index) => (
          <CoverageVectorCell
            value={[
              services[index]?.inNetwork?.emergencyRoom,
              services[index]?.outOfNetwork?.emergencyRoom ||
                OutOfNetworkDefaultValue(
                  services[index]?.outOfNetwork,
                  fundingType,
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
    </>
  );
};

export default Medical;
